import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { MagnifyingGlass, Trash, Upload, Plus, Phone, ArrowLeft, ArrowRight, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERY_OPTIONS = [
  { value: "express_post", label: "Express Post", fee: 12 },
  { value: "own_courier", label: "Own Courier / Driver", fee: 0 },
  { value: "urgent_express", label: "URGENT + Express Post", fee: 32 },
  { value: "urgent_courier", label: "URGENT + Own Courier", fee: 20 },
  { value: "pickup", label: "Pickup from Amcal Newmarket", fee: 0 },
];

export default function NewOrderPage() {
  const { user, activeOrg, activeOrgId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const showWsp = user?.role === "master_admin";
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Product selection
  const [products, setProducts] = useState([]);
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [cart, setCart] = useState([]);

  // Patient details
  const [patientName, setPatientName] = useState("");
  const [patientPhone, setPatientPhone] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("express_post");
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [notes, setNotes] = useState("");

  // Files
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Patient autocomplete
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const fetchPatientSuggestions = async (q) => {
    try { const { data } = await axios.get(`${API}/patients/search?q=${encodeURIComponent(q)}`, { withCredentials: true }); setPatientSuggestions(data.patients); } catch { /* */ }
  };

  // Handle reorder data from navigation state
  useEffect(() => {
    const reorderData = location.state?.reorderData;
    if (reorderData) {
      if (reorderData.items) setCart(reorderData.items);
      if (reorderData.patient_name) setPatientName(reorderData.patient_name);
      if (reorderData.patient_phone) setPatientPhone(reorderData.patient_phone);
      if (reorderData.delivery_method) setDeliveryMethod(reorderData.delivery_method);
      toast.success("Previous order items loaded. Review and submit.");
    }
  }, [location.state]);

  useEffect(() => {
    axios.get(`${API}/products/types`, { withCredentials: true }).then(r => setTypes(r.data.types)).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    const params = new URLSearchParams({ limit: 200 });
    if (filterType !== "all") params.append("type", filterType);
    if (search) params.append("search", search);
    if (activeOrgId) params.append("org_id", activeOrgId);
    try {
      const { data } = await axios.get(`${API}/products?${params}`, { withCredentials: true });
      setProducts(data.products);
    } catch { /* ignore */ }
  }, [filterType, search, activeOrgId]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Default selected address to first verified address
  useEffect(() => {
    if (activeOrg && !selectedAddressId) {
      const verified = (activeOrg.addresses || []).filter(a => a.verified);
      if (verified.length > 0) setSelectedAddressId(verified[0].id);
    }
  }, [activeOrg, selectedAddressId]);

  const addToCart = (product) => {
    const price = showWsp ? (product.wsp || 0) : (product.rrp || 0);
    setCart(prev => [...prev, {
      product_id: product.id,
      type: product.type,
      ingredient: product.ingredient,
      strength: product.strength,
      qty: product.qty,
      price,
      quote_required: product.quote_required
    }]);
    toast.success(`Added ${product.ingredient} to order`);
  };

  const removeFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const deliveryFee = DELIVERY_OPTIONS.find(d => d.value === deliveryMethod)?.fee || 0;
  const subtotal = cart.reduce((sum, item) => sum + (item.quote_required ? 0 : item.price), 0);
  const total = subtotal + deliveryFee;

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        setUploadedFiles(prev => [...prev, data]);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (cart.length === 0) { toast.error("Add at least one product"); return; }
    if (!activeOrgId) { toast.error("No active organization selected"); return; }
    if (!selectedAddressId) { toast.error("Pick a delivery address"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, {
        org_id: activeOrgId,
        address_id: selectedAddressId,
        patient_name: patientName,
        patient_phone: patientPhone,
        items: cart,
        delivery_method: deliveryMethod,
        delivery_fee: deliveryFee,
        notes,
        prescription_file_ids: uploadedFiles.map(f => f.id)
      }, { withCredentials: true });
      toast.success("Order submitted successfully!");
      navigate("/orders");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit order");
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="new-order-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">NEW ORDER</p>
        <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Place an Order</h1>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center gap-2 mb-8" data-testid="step-indicators">
        {["Select Products", "Delivery & Notes", "Upload Script", "Review & Submit"].map((label, i) => (
          <button
            key={i}
            onClick={() => setStep(i + 1)}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${
              step === i + 1 ? "bg-[#0055FF] text-white" : step > i + 1 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-zinc-100 text-zinc-500"
            }`}
            data-testid={`step-${i + 1}`}
          >
            <span>{i + 1}</span>
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Step 1: Product Selection */}
      {step === 1 && (
        <div data-testid="step-1-content">
          {/* Cart Summary */}
          {cart.length > 0 && (
            <div className="border border-[#0055FF] bg-blue-50/30 p-4 mb-4" data-testid="cart-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-zinc-950">{cart.length} item{cart.length !== 1 ? "s" : ""} in order</p>
                <p className="text-sm font-mono font-bold text-[#0055FF]">${subtotal.toFixed(2)}</p>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-none" data-testid="order-search-input" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48 rounded-none" data-testid="order-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="border border-zinc-200 max-h-[400px] overflow-y-auto">
            <table className="w-full border-collapse text-left">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <th className="border-b-2 border-zinc-950 py-2 px-3 font-mono text-xs uppercase tracking-wider text-zinc-500">Ingredient</th>
                  <th className="border-b-2 border-zinc-950 py-2 px-3 font-mono text-xs uppercase tracking-wider text-zinc-500">Strength</th>
                  <th className="border-b-2 border-zinc-950 py-2 px-3 font-mono text-xs uppercase tracking-wider text-zinc-500">Qty</th>
                  <th className="border-b-2 border-zinc-950 py-2 px-3 font-mono text-xs uppercase tracking-wider text-zinc-500">Price</th>
                  <th className="border-b-2 border-zinc-950 py-2 px-3 font-mono text-xs uppercase tracking-wider text-zinc-500 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => {
                  const price = showWsp ? p.wsp : p.rrp;
                  const restricted = p.restricted;
                  return (
                    <tr key={p.id || i} className={`border-b border-zinc-200 transition-colors ${restricted ? "bg-zinc-50/60 opacity-60" : "hover:bg-zinc-50"}`}>
                      <td className="py-2 px-3 text-sm">
                        <span className="text-zinc-800 font-medium">{p.ingredient}</span>
                        <span className="text-zinc-400 text-xs ml-2">{p.type}</span>
                      </td>
                      <td className="py-2 px-3 text-sm text-zinc-600 font-mono">{p.strength}</td>
                      <td className="py-2 px-3 text-sm text-zinc-600">{p.qty}</td>
                      <td className="py-2 px-3 text-sm font-semibold">
                        {p.quote_required ? (
                          <span className="text-amber-600 flex items-center gap-1"><Phone size={12} />Quote</span>
                        ) : price != null ? `$${price.toFixed(2)}` : "-"}
                      </td>
                      <td className="py-2 px-3">
                        <button
                          onClick={() => addToCart(p)}
                          disabled={restricted}
                          title={restricted ? "Not available for your account type" : ""}
                          className="w-8 h-8 flex items-center justify-center bg-[#0055FF] text-white hover:bg-[#0044CC] transition-colors active:scale-[0.95] disabled:bg-zinc-300 disabled:cursor-not-allowed disabled:hover:bg-zinc-300"
                          data-testid={restricted ? `add-product-${i}-restricted` : `add-product-${i}`}
                        >
                          <Plus size={14} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Cart Items */}
          {cart.length > 0 && (
            <div className="mt-6 border border-zinc-200" data-testid="cart-items">
              <div className="p-3 border-b border-zinc-200 bg-zinc-50">
                <h3 className="font-[Chivo] font-bold text-zinc-950 text-sm">Order Items</h3>
              </div>
              {cart.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 border-b border-zinc-100 last:border-b-0" data-testid={`cart-item-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-800 font-medium truncate">{item.ingredient}</p>
                    <p className="text-xs text-zinc-500">{item.strength} - {item.qty}</p>
                  </div>
                  <div className="flex items-center gap-3 ml-2">
                    <span className="text-sm font-mono font-semibold">
                      {item.quote_required ? "Quote" : `$${item.price.toFixed(2)}`}
                    </span>
                    <button onClick={() => removeFromCart(i)} className="text-zinc-400 hover:text-red-500 transition-colors" data-testid={`remove-cart-item-${i}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button onClick={() => setStep(2)} disabled={cart.length === 0} className="bg-[#0055FF] text-white font-semibold px-6 py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98] disabled:opacity-50 flex items-center gap-2" data-testid="step-1-next">
              Next <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Delivery & Notes */}
      {step === 2 && (
        <div className="max-w-lg space-y-5" data-testid="step-2-content">
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Patient Name (optional)</Label>
            <div className="relative">
              <Input
                value={patientName}
                onChange={e => { setPatientName(e.target.value); if (e.target.value.length >= 2) fetchPatientSuggestions(e.target.value); else setPatientSuggestions([]); }}
                className="mt-1.5 rounded-none"
                data-testid="patient-name-input"
                placeholder="Start typing to search previous patients..."
                autoComplete="off"
              />
              {patientSuggestions.length > 0 && (
                <div className="absolute z-20 w-full border border-zinc-200 bg-white shadow-lg mt-0.5 max-h-40 overflow-y-auto">
                  {patientSuggestions.map((p, i) => (
                    <button key={i} onClick={() => { setPatientName(p.name); setPatientSuggestions([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex justify-between" data-testid={`patient-suggestion-${i}`}>
                      <span className="text-zinc-800 font-medium">{p.name}</span>
                      <span className="text-xs text-zinc-400">{p.order_count} orders</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Delivery Address</Label>
            {(() => {
              const verified = (activeOrg?.addresses || []).filter(a => a.verified);
              if (verified.length === 0) {
                return <p className="mt-1.5 p-3 text-xs bg-amber-50 border border-amber-200 text-amber-800" data-testid="no-verified-addr">No verified addresses yet. Org admin: add one in Addresses tab and wait for Amcal verification.</p>;
              }
              if (verified.length === 1) {
                const a = verified[0];
                return <div className="mt-1.5 p-3 border border-zinc-200 bg-zinc-50 text-sm" data-testid="single-addr-display">
                  <p className="font-medium text-zinc-950">{a.label || a.street_line1}</p>
                  <p className="text-zinc-700">{a.street_line1}{a.street_line2 ? `, ${a.street_line2}` : ""}, {a.suburb} {a.state} {a.postcode}</p>
                </div>;
              }
              return <Select value={selectedAddressId} onValueChange={setSelectedAddressId}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="address-select"><SelectValue placeholder="Pick delivery address" /></SelectTrigger>
                <SelectContent>
                  {verified.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label || a.street_line1} — {a.suburb} {a.state} {a.postcode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>;
            })()}
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Delivery Method</Label>
            <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
              <SelectTrigger className="mt-1.5 rounded-none" data-testid="delivery-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_OPTIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label} {d.fee > 0 ? `($${d.fee})` : "(Free)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Notes / Special Instructions</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="mt-1.5 rounded-none" data-testid="order-notes-input" />
          </div>
          <div className="flex gap-3 pt-4">
            <button onClick={() => setStep(1)} className="bg-zinc-100 text-zinc-950 font-semibold px-6 py-3 border border-zinc-200 hover:bg-zinc-200 transition-colors flex items-center gap-2" data-testid="step-2-back">
              <ArrowLeft size={16} /> Back
            </button>
            <button onClick={() => setStep(3)} className="bg-[#0055FF] text-white font-semibold px-6 py-3 hover:bg-[#0044CC] transition-colors flex items-center gap-2" data-testid="step-2-next">
              Next <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Upload Script */}
      {step === 3 && (
        <div className="max-w-lg" data-testid="step-3-content">
          <p className="text-sm text-zinc-600 mb-4">Upload a photo of the prescription / script. Accepted formats: JPG, PNG, PDF.</p>
          <label className="border-2 border-dashed border-zinc-300 p-12 text-center hover:border-[#0055FF] hover:bg-blue-50/30 transition-colors cursor-pointer block" data-testid="file-upload-area">
            <Upload size={32} className="mx-auto text-zinc-400 mb-3" />
            <p className="text-sm text-zinc-600 font-medium">{uploading ? "Uploading..." : "Click to upload prescription photo"}</p>
            <p className="text-xs text-zinc-400 mt-1">JPG, PNG, PDF up to 10MB</p>
            <input type="file" accept="image/*,.pdf" multiple onChange={handleFileUpload} className="hidden" data-testid="file-input" />
          </label>
          {uploadedFiles.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="uploaded-files-list">
              {uploadedFiles.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-zinc-200 bg-zinc-50">
                  <span className="text-sm text-zinc-800 truncate">{f.original_filename}</span>
                  <Check size={16} className="text-emerald-600" />
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3 mt-6">
            <button onClick={() => setStep(2)} className="bg-zinc-100 text-zinc-950 font-semibold px-6 py-3 border border-zinc-200 hover:bg-zinc-200 transition-colors flex items-center gap-2" data-testid="step-3-back">
              <ArrowLeft size={16} /> Back
            </button>
            <button onClick={() => setStep(4)} className="bg-[#0055FF] text-white font-semibold px-6 py-3 hover:bg-[#0044CC] transition-colors flex items-center gap-2" data-testid="step-3-next">
              Review Order <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 4 && (
        <div className="max-w-2xl" data-testid="step-4-content">
          <div className="border border-zinc-200">
            <div className="p-4 border-b border-zinc-200 bg-zinc-50">
              <h3 className="font-[Chivo] font-bold text-zinc-950">Order Summary</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Delivery</p>
                <p className="text-sm text-zinc-950 mt-1">{DELIVERY_OPTIONS.find(d => d.value === deliveryMethod)?.label} {deliveryFee > 0 ? `($${deliveryFee})` : "(Free)"}</p>
              </div>
              {notes && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Notes</p>
                  <p className="text-sm text-zinc-800 mt-1">{notes}</p>
                </div>
              )}
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Items ({cart.length})</p>
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                  {cart.map((item, i) => (
                    <div key={i} className="flex justify-between p-3">
                      <div>
                        <p className="text-sm font-medium text-zinc-800">{item.ingredient}</p>
                        <p className="text-xs text-zinc-500">{item.strength} - {item.qty}</p>
                      </div>
                      <span className="text-sm font-mono font-semibold">{item.quote_required ? "Quote Req." : `$${item.price.toFixed(2)}`}</span>
                    </div>
                  ))}
                </div>
              </div>
              {uploadedFiles.length > 0 && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Prescriptions</p>
                  <p className="text-sm text-zinc-800 mt-1">{uploadedFiles.length} file(s) attached</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-zinc-200 bg-zinc-50">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-zinc-500">Subtotal</span>
                <span className="font-mono">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-zinc-500">Delivery</span>
                <span className="font-mono">${deliveryFee.toFixed(2)}</span>
              </div>
              {cart.some(i => i.quote_required) && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-amber-600">Items requiring quote</span>
                  <span className="text-amber-600 font-mono">{cart.filter(i => i.quote_required).length}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold mt-2 pt-2 border-t border-zinc-200">
                <span>Total</span>
                <span className="font-mono">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => setStep(3)} className="bg-zinc-100 text-zinc-950 font-semibold px-6 py-3 border border-zinc-200 hover:bg-zinc-200 transition-colors flex items-center gap-2" data-testid="step-4-back">
              <ArrowLeft size={16} /> Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#0055FF] text-white font-semibold px-8 py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98] disabled:opacity-50 flex items-center gap-2"
              data-testid="submit-order-button"
            >
              {submitting ? "SUBMITTING..." : "SUBMIT ORDER"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
